import { useMemo } from 'react'
import { parseISO, isBefore, isValid } from 'date-fns'

import { useGetMyApplications, useListRenewals } from '@/utils/api/membership-service'
import type { ApplicationResponseDTO } from '@/utils/api/membership-service/models'

function sortApplicationsByRecency(applications: ApplicationResponseDTO[]): ApplicationResponseDTO[] {
  return [...applications].sort((a, b) => {
    const getDate = (application: ApplicationResponseDTO) => {
      const reference = application.updatedAt ?? application.submittedAt ?? application.createdAt ?? 0
      return new Date(reference).getTime()
    }

    return getDate(b) - getDate(a)
  })
}

interface RenewalStatus {
  latestApplication: ApplicationResponseDTO | null
  membershipExpiry: Date | null
  membershipExpired: boolean
  canRenew: boolean
  hasPendingRenewal: boolean
  reason?: string
  isLoading: boolean
  isError: boolean
  refetch: () => Promise<void>
}

export function useMemberRenewalStatus(): RenewalStatus {
  const applicationsQuery = useGetMyApplications(
    { page: 0, size: 25 },
    {
      query: {
        staleTime: 60_000,
        select: (response) => response.data,
      },
    },
  )

  const latestApplication = useMemo<ApplicationResponseDTO | null>(() => {
    const payload = applicationsQuery.data
    if (!payload) return null

    const applications = Array.isArray(payload.content)
      ? (payload.content.filter(Boolean) as ApplicationResponseDTO[])
      : []

    if (applications.length === 0) {
      return null
    }

    return sortApplicationsByRecency(applications)[0] ?? null
  }, [applicationsQuery.data])

  const applicationId = latestApplication?.id ?? 0

  // Fetch renewals to check for pending status
  const renewalsQuery = useListRenewals(
    applicationId,
    { page: 0, size: 50 },
    {
      query: {
        enabled: Boolean(latestApplication?.id),
        staleTime: 60_000,
        select: (response) => response.data,
      },
    },
  )

  const hasPendingRenewal = useMemo(() => {
    const renewalsData = renewalsQuery.data
    if (!renewalsData) return false

    // Extract renewals array from various possible response structures
    let renewals: unknown[] = []
    if (Array.isArray(renewalsData)) {
      renewals = renewalsData
    } else if (typeof renewalsData === 'object') {
      const record = renewalsData as Record<string, unknown>
      if (Array.isArray(record.content)) {
        renewals = record.content
      } else if (Array.isArray(record.data)) {
        renewals = record.data
      }
    }

    // Check if any renewal has PENDING status
    return renewals.some((renewal) => {
      if (renewal && typeof renewal === 'object') {
        const renewalRecord = renewal as Record<string, unknown>
        const status = typeof renewalRecord.status === 'string' ? renewalRecord.status : ''
        return status.toUpperCase() === 'PENDING'
      }
      return false
    })
  }, [renewalsQuery.data])

  const membershipExpiry = useMemo(() => {
    if (!latestApplication?.membershipExpiryAt) {
      return null
    }
    try {
      const parsed = parseISO(latestApplication.membershipExpiryAt)
      return isValid(parsed) ? parsed : null
    } catch {
      return null
    }
  }, [latestApplication?.membershipExpiryAt])

  const now = new Date()
  let membershipExpired = false
  if (membershipExpiry) {
    membershipExpired = isBefore(membershipExpiry, now)
  } else if (latestApplication) {
    membershipExpired = true
  }

  const canRenew = Boolean(latestApplication?.id) && membershipExpired && !hasPendingRenewal

  let reason: string | undefined

  if (applicationsQuery.isError) {
    reason =
      applicationsQuery.error instanceof Error
        ? applicationsQuery.error.message
        : 'Unable to load your membership status.'
  }

  if (!reason) {
    if (!latestApplication) {
      reason = 'We could not find a submitted membership application.'
    } else if (hasPendingRenewal) {
      reason = 'Your renewal request is pending approval. You will be notified once it has been reviewed.'
    } else if (!membershipExpired) {
      const formatted = membershipExpiry?.toLocaleDateString() ?? 'a future date'
      reason = `Your membership is active until ${formatted}.`
    }
  }

  const isLoading = applicationsQuery.isPending || renewalsQuery.isPending

  return {
    latestApplication,
    membershipExpiry,
    membershipExpired,
    canRenew,
    hasPendingRenewal,
    reason,
    isLoading,
    isError: applicationsQuery.isError || renewalsQuery.isError,
    refetch: async () => {
      await Promise.all([
        applicationsQuery.refetch(),
        renewalsQuery.refetch(),
      ])
    },
  }
}
